import { ApolloLink, HttpLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { WebSocketLink } from '@apollo/link-ws'
import { parse } from 'cookie'
import { NextPageContext } from 'next'

import {
  getIsomorphicXOrganizationSlugHeader,
  hasAuthorizationError,
  hasVerificationError,
  logLinkErrors,
} from './utils'

import { logout } from '~lib/auth'
import { AUTH_DATA_KEYS } from '~lib/constants'
import { getIsomorphicCookie, isBrowser } from '~lib/helpers'

/**
 * Apollo http link to be used in private routes
 */
const privateHttpLink = new HttpLink({
  uri: `${process.env.NEXT_PUBLIC_API_HOST}/graphql`,
})

/**
 * Apollo error link to be used in private routes
 */
const privateErrorLink = onError((error) => {
  logLinkErrors(error)

  if (isBrowser() && (hasAuthorizationError(error) || hasVerificationError(error))) {
    logout()
  }
})

/**
 * Create isomorphic (client side & server side) apollo link extened with headers required for private page
 */
export const createPrivateIsomorphicAuthLink = (ctx: NextPageContext | undefined) => {
  return setContext((_, { headers }) => {
    const cookies = getIsomorphicCookie(ctx?.req)

    const token = cookies[AUTH_DATA_KEYS.ACCESS_TOKEN]
    const organizationId = cookies[AUTH_DATA_KEYS.ORGANIZATION_ID]

    const organizationSlug = getIsomorphicXOrganizationSlugHeader(ctx) || cookies[AUTH_DATA_KEYS.ORGANIZATION_SLUG] // FIXME refactor auth pages and routes
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        organizationId, // can we remove it?
        organizationSlug, // TODO rename to X-Organization-Slug
      },
    }
  })
}

/**
 * Create apollo websocket link extened with connection params required for private page
 */
export const createPrivateWebSocketLink = () => {
  if (!isBrowser()) return null

  return new WebSocketLink({
    uri: process.env.NEXT_PUBLIC_WS_SUBSCRIPTION_URL,
    options: {
      reconnect: true,
      connectionParams: () => {
        // passing undefined is fine on client
        const organizationSlug = getIsomorphicXOrganizationSlugHeader(undefined)

        const cookies = parse(document.cookie)
        const token = cookies[AUTH_DATA_KEYS.ACCESS_TOKEN]
        const organizationId = cookies[AUTH_DATA_KEYS.ORGANIZATION_ID]
        return {
          authorization: `Bearer ${token}`,
          organizationId, // FIXME it's still used on backend side
          organizationSlug,
        }
      },
    },
  })
}

/**
 * Creates private page apollo link to be used in private routes
 */
export const createPrivateApolloLink = (ctx: NextPageContext | undefined) => {
  return ApolloLink.from([createPrivateIsomorphicAuthLink(ctx), privateErrorLink, privateHttpLink])
}
